import React, { useEffect, useRef, useState, ComponentProps } from 'react';
import classNames from 'classnames';
import { Navigation, NavigationItemType, Tooltip } from '@fx-ui/fine-design';
import { DoubleLeftOutlined, DoubleRightOutlined } from '@fx-ui/fine-design/icons';
import { IconButton } from '~/components';
import { get } from '~/utils';
import { usePerfectScrollbar } from '~/hooks';
import Intl from '@fx-ui/intl';

interface ICollapsibleNavigationProps {
    mode?: ComponentProps<typeof Navigation>['mode'];
    className?: string;
    style?: React.CSSProperties;
    collapsedWidth?: string; // 收起时导航栏宽度，若为undefined，取组件库默认收起宽度
    expandedWidth?: string; // 展开时导航栏宽度，若为undefined，展开时导航栏宽度根据导航各项文字宽度自适应
    items: NavigationItemType[];
    selectedKeys?: string[]; // 初始选中的item
    defaultExpand?: boolean; // 初始是否展开，不传则默认 true
    onNavigationClick?: (key: string) => void; // 点击导航栏某一项
    onRetractBtnClick?: (currentState: boolean) => void; // 点击展开收起按钮
}

const CollapsibleNavigation: React.FunctionComponent<ICollapsibleNavigationProps> = (
    props: ICollapsibleNavigationProps
) => {
    const {
        mode = 'inline',
        className,
        style,
        collapsedWidth,
        expandedWidth,
        items,
        selectedKeys,
        defaultExpand = true,
        onNavigationClick,
        onRetractBtnClick
    } = props;
    const [isExpand, setExpand] = useState(defaultExpand);
    const navigationContainerRef = useRef<HTMLDivElement>(null);
    const { initOrUpdateScrollbar } = usePerfectScrollbar(() => navigationContainerRef.current);

    useEffect(() => {
        initOrUpdateScrollbar();
    }, [isExpand]);

    return (
        <div
            className={classNames('fx-collapsible-navigation', className, {
                collapse: !isExpand,
                expand: isExpand
            })}
        >
            <Tooltip
                content={isExpand ? Intl.get('sidebar.fold') : Intl.get('sidebar.unfold')}
                placement="top"
                mouseLeaveDelay={0}
            >
                <div
                    className="sidebar-toggle"
                    onClick={() => {
                        setExpand(!isExpand);
                        onRetractBtnClick?.(isExpand);
                    }}
                >
                    <IconButton
                        className="btn"
                        icon={
                            isExpand ? (
                                <DoubleLeftOutlined className="icon" />
                            ) : (
                                <DoubleRightOutlined className="icon" />
                            )
                        }
                    />
                </div>
            </Tooltip>
            <div
                ref={navigationContainerRef}
                className="collapsible-navigation"
                style={{
                    width: isExpand ? expandedWidth : collapsedWidth
                }}
                onMouseEnter={() => {
                    initOrUpdateScrollbar();
                }}
            >
                <Navigation
                    theme="clear"
                    mode={mode}
                    style={{
                        ...style
                    }}
                    inlineCollapsedWidth={collapsedWidth}
                    items={items}
                    inlineCollapsed={!isExpand}
                    selectedKeys={selectedKeys}
                    onClick={(info) => {
                        const key = get(info, 'key', '');
                        onNavigationClick?.(key);
                    }}
                />
            </div>
        </div>
    );
};

export default CollapsibleNavigation;
